import cn from 'classnames';
import { isEmpty } from 'ramda';
import React, { FC, memo, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { generatePath, useLocation, useNavigate } from 'react-router';

import { ReportType } from '@/shared/api/protocol-ts/model/dto_report_pb';
import { Tooth_SuggestedTreatmentStatus } from '@/shared/api/protocol-ts/model/dto_report_tooth_pb';
import {
  DEFAULT_MEDICAL_IMAGE_HEIGHT,
  DEFAULT_MEDICAL_IMAGE_HEIGHT_MOBILE,
  LocationStateType,
  PATHS,
} from '@/shared/config';
import {
  SelectedSlicesMIR,
  SlicesMIR,
} from '@/shared/graphics/medicalImageRender/MedicalImageRender';
import { useAppDispatch, useAppSelector, useMedia } from '@/shared/hooks';
import { scrollToElementByID, scrollToTop, trackEvent } from '@/shared/lib';
import { Button, Skeleton, Tooltip, WidgetCard } from '@/shared/ui';

import {
  CompactCondition,
  ConditionButton,
  ConditionGroups,
  conditionModel,
  useToothConditions,
} from '@/entities/condition';
import { ModalID, modalModel } from '@/entities/modal';
import { organizationModel } from '@/entities/organization';
import { FMXNavigationMode, reportsModel } from '@/entities/reports';
import { getDisplayToothNumber, toothModel } from '@/entities/tooth';

import { useFDA3DGPRestrictions } from '@/features/fdaRestrictions';
import { useAddAndRemoveMedicalImage } from '@/features/medicalImage';
import { useUpdateConditionDecision } from '@/features/updateConditionDecision';

import type { RemoveAllSlicesControl } from '../../config';
import { useCommentTooth, useResetTooth } from '../../hooks';
import { ApproveToothButton } from '../ApproveToothButton/ApproveToothButton';
import { CommentTooth } from '../CommentTooth/CommentTooth';
import { ConditionTitle } from '../ContitionTitle/ConditionTitle';

import styles from './ToothCard.module.scss';

type ToothCardProps = {
  toothID: string;
  className?: string;
  imageContainerComponent?: React.ReactNode;
  removeAllSlicesControl?: RemoveAllSlicesControl;
  showViewerButton?: boolean;
  showRemoveAllSlices?: boolean;
};

export const InternalToothCard: FC<ToothCardProps> = (props) => {
  const {
    toothID,
    className,
    imageContainerComponent,
    removeAllSlicesControl,
    showViewerButton = true,
    showRemoveAllSlices = false,
  } = props;

  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { formatMessage } = useIntl();
  const { isMobile } = useMedia();

  const report = useAppSelector(reportsModel.selectors.selectCurrentReport);
  const tooth = useAppSelector((state) =>
    toothModel.selectors.selectById(state, toothID),
  );
  const conditionLoading = useAppSelector(
    conditionModel.selectors.selectLoading,
  );
  const navigationMode = useAppSelector(
    reportsModel.selectors.selectNavigationMode,
  );
  const activeToothID = useAppSelector(
    reportsModel.selectors.selectActiveToothID,
  );
  const dentalNotationFormat = useAppSelector(
    organizationModel.selectors.selectDentalNotationFormat,
  );
  const showConditionDetailsFromSettings = useAppSelector(
    reportsModel.selectors.selectShowConditionDetails,
  );

  const { shouldHideProbabilities } = useFDA3DGPRestrictions();

  const showConditionDetails = shouldHideProbabilities
    ? false
    : showConditionDetailsFromSettings;

  const displayComment = useAppSelector(
    toothModel.selectors.selectToothCommentForDisplay(toothID),
  );
  const displaySlices = useAppSelector(
    toothModel.selectors.selectDisplaySlicesByToothID(toothID),
  );

  const { removeAllMedicalImagesFromSelected, isRemovingAllSlices } =
    useAddAndRemoveMedicalImage();

  const isFMXToothChartMode = navigationMode === FMXNavigationMode.ToothChart;

  const { PatientID: patientID, ID: reportID, Type: reportType } = report ?? {};

  const {
    Numeration: toothNumeration,
    IsApproved: isToothApproved,
    SuggestedTreatment: toothSuggestedTreatment,
  } = tooth ?? {};

  const ISONumber = toothNumeration?.ISO;

  const showSlices = reportType === ReportType.ReportType_CBCT_GP;
  const isShortCard = !showSlices;

  const displayToothNumber = getDisplayToothNumber(
    ISONumber ?? 0,
    dentalNotationFormat,
  ).toString();

  const isSupernumerary = toothNumeration?.SupernumeraryIndex ?? 0 > 0;
  const titleToothNumber = displayToothNumber + (isSupernumerary ? ' *' : '');

  const locationState = location?.state as LocationStateType;

  const isReportWithViewModes =
    reportType === ReportType.ReportType_IOXRay_GP ||
    reportType === ReportType.ReportType_Pano_Bitewings;

  const isToothFocused = toothID === activeToothID;
  const isFocusedView = isReportWithViewModes
    ? isToothFocused && isFMXToothChartMode
    : isToothFocused;

  const buttonSize = isMobile ? 'small' : 'medium';
  const areSlicesSelected =
    removeAllSlicesControl?.hasSelectedSlices ?? displaySlices.length > 0;
  const shouldShowRemoveAllSlices = showRemoveAllSlices && areSlicesSelected;
  const isRemoveAllSlicesLoading =
    removeAllSlicesControl?.isLoading ??
    (removeAllSlicesControl ? false : isRemovingAllSlices);

  const toothTitleClickHandle = () => {
    if (isFMXToothChartMode && !isFocusedView) {
      dispatch(reportsModel.actions.setActiveToothID(toothID));

      scrollToElementByID(toothID, 'smooth', 'start');
    }
  };

  const { anatomyConditionItems, generalConditionItems, conditionIsLoaded } =
    useToothConditions({
      toothID,
    });

  const toothTitleCondition = anatomyConditionItems.find(
    (item) => item.group === ConditionGroups.toothType,
  );

  const filteredAnatomyConditionItems = anatomyConditionItems.filter(
    (item) => item.group !== ConditionGroups.toothType,
  );

  const filteredGeneralConditionItems = generalConditionItems;

  const isGeneralConditionItemsExist = filteredGeneralConditionItems.length > 0;

  const { updateConditionDecision } = useUpdateConditionDecision(toothID);

  const {
    newComment,
    isCommentEditorVisible,
    isNewCommentLoading,
    closeAndResetTextInEditor,
    openChangeComment,
    changeCommentInEditor,
    saveComment,
  } = useCommentTooth(displayComment, toothID);

  const { resetTooth, isResetToothLoading } = useResetTooth(toothID);

  const isToothPage = location.pathname.includes('/tooth/');

  const handleAddCondition = useCallback(
    (conditionID?: string) => {
      trackEvent({
        eventName: isToothPage
          ? 'ToothPageAddConditionsButtonClicked'
          : 'ReportPageAddConditionsButtonClicked',
        properties: {
          tooth_number: ISONumber,
        },
      });

      if (report) {
        dispatch(
          modalModel.actions.openModal({
            modalID: ModalID.AddCondition,
            data: {
              toothID,
              displayToothNumber,
              reportID: reportID ?? '',
              ISONumber,
              conditionID,
            },
          }),
        );
      }
    },
    [reportID, toothID, displayToothNumber],
  );

  const handle3DSlicesClick = () => {
    trackEvent({
      eventName: 'ReportPageToothSlicesButtonClicked',
      properties: {
        tooth_number: ISONumber,
      },
    });

    scrollToTop();
    navigate(
      generatePath(PATHS.gpReportAllSlices, {
        patientID,
        reportID,
        toothID,
      }),
      {
        state: {
          ...locationState,
          toothID,
        },
        relative: 'path',
      },
    );
  };

  const onConditionHoverHandler = (conditionItem?: CompactCondition) => {
    if (!conditionItem) {
      dispatch(conditionModel.actions.setHoveredConditionIDs([]));
    } else if (conditionItem.hasLocalizations) {
      dispatch(
        conditionModel.actions.setHoveredConditionIDs(conditionItem.ids),
      );
    }
  };

  const onToothTitleHover = (conditionItem?: CompactCondition) => {
    if (!conditionItem) {
      dispatch(toothModel.actions.setHoveredToothID(''));
    } else {
      dispatch(toothModel.actions.setHoveredToothID(toothID));
    }
  };

  const handleRemoveAllSlices = () => {
    if (removeAllSlicesControl) {
      removeAllSlicesControl.onRemove();
      return;
    }

    removeAllMedicalImagesFromSelected(toothID);
  };

  if (conditionLoading === 'pending') {
    return (
      <div>
        <Skeleton width="100%" height={160} borderRadius={16} />
      </div>
    );
  }

  const handleOpenModal = (assetID: string) => {
    if (!tooth) {
      return;
    }

    dispatch(
      modalModel.actions.openModal({
        modalID: ModalID.MIRenderModal,
        data: { assetID, toothID },
      }),
    );
  };

  if (!toothTitleCondition) {
    return null;
  }

  return (
    <WidgetCard
      id={toothID}
      data-component="tooth-card"
      data-id={toothID}
      data-tooth-number={ISONumber ? String(ISONumber) : undefined}
      data-testid={ISONumber ? `tooth-card-${ISONumber}` : undefined}
      className={cn(
        styles.container,
        isFocusedView && styles.focused,
        className,
      )}
    >
      <header className={styles.header}>
        {toothTitleCondition && (
          <ConditionTitle
            isFMXToothChartMode={isFMXToothChartMode}
            onHoverHandle={!activeToothID ? onToothTitleHover : () => {}}
            onClickHandle={toothTitleClickHandle}
            conditionItem={toothTitleCondition}
            toothNumber={titleToothNumber}
          />
        )}

        {isEmpty(anatomyConditionItems) && !conditionIsLoaded && (
          <Skeleton.Filter
            filtersQuantity={2}
            width={100}
            height={isMobile ? 28 : 36}
            gap={4}
          />
        )}

        {!isMobile && filteredAnatomyConditionItems.length > 0 && (
          <div className={styles.conditions}>
            {filteredAnatomyConditionItems.map((conditionItem) => (
              <ConditionButton
                key={conditionItem.id}
                conditionInterface={conditionItem}
                onClick={handleAddCondition}
                updateConditionDecision={updateConditionDecision}
                showProbability={showConditionDetails}
              />
            ))}
          </div>
        )}

        <Tooltip.Primary
          content={formatMessage({
            id: 'toothCard.restoreConditionsButton',
            defaultMessage: 'Restore the original conditions',
          })}
          side="bottom"
          triggerClassName={styles.restoreButtonWrapper}
        >
          <Button
            size={buttonSize}
            variant="gray"
            icon="back"
            data-component="tooth-restore-button"
            onClick={resetTooth}
            loading={isResetToothLoading}
          />
        </Tooltip.Primary>
      </header>

      {isMobile && filteredAnatomyConditionItems.length > 0 && (
        <div className={styles.conditions}>
          {filteredAnatomyConditionItems.map((conditionItem) => (
            <ConditionButton
              key={conditionItem.id}
              conditionInterface={conditionItem}
              onClick={handleAddCondition}
              updateConditionDecision={updateConditionDecision}
              showProbability={showConditionDetails}
            />
          ))}
        </div>
      )}

      {(isGeneralConditionItemsExist || isShortCard) && (
        <div className={cn(styles.conditions)}>
          {isGeneralConditionItemsExist &&
            filteredGeneralConditionItems.map((conditionItem, index) => {
              const isLastGeneralCondition =
                index === filteredGeneralConditionItems.length - 1;
              const childConditions = conditionItem?.childConditionInterfaces;
              const hasChildConditions = childConditions?.length > 0;

              return (
                <div key={conditionItem.id} className={styles.conditionItem}>
                  <ConditionButton
                    onHoverHandle={onConditionHoverHandler}
                    updateConditionDecision={updateConditionDecision}
                    conditionInterface={conditionItem}
                    onClick={handleAddCondition}
                    showProbability={showConditionDetails}
                  />
                  {isShortCard &&
                    isLastGeneralCondition &&
                    !hasChildConditions && (
                      <Button
                        size={isMobile ? 'small' : 'medium'}
                        variant="gray"
                        icon="plus"
                        onClick={() => handleAddCondition()}
                      />
                    )}
                  {childConditions?.map((childConditionInterface) => (
                    <ConditionButton
                      updateConditionDecision={updateConditionDecision}
                      onHoverHandle={onConditionHoverHandler}
                      key={childConditionInterface.id}
                      conditionInterface={childConditionInterface}
                      onClick={handleAddCondition}
                      showProbability={showConditionDetails}
                      isChild
                    />
                  ))}
                  {isShortCard &&
                    isLastGeneralCondition &&
                    hasChildConditions && (
                      <Button
                        size={isMobile ? 'small' : 'medium'}
                        variant="gray"
                        icon="plus"
                        onClick={() => handleAddCondition()}
                      />
                    )}
                </div>
              );
            })}

          {isShortCard && !isGeneralConditionItemsExist && (
            <Button
              size={isMobile ? 'small' : 'medium'}
              variant="gray"
              icon="plus"
              onClick={() => handleAddCondition()}
            >
              <FormattedMessage
                id="toothCard.openAddConditionModalButton"
                defaultMessage="Condition"
              />
            </Button>
          )}
        </div>
      )}

      {showSlices && tooth && (
        <div className={styles.slicesSection}>
          {shouldShowRemoveAllSlices && (
            <Button
              variant="tertiary"
              size="small"
              icon="delete"
              loading={isRemoveAllSlicesLoading}
              disabled={removeAllSlicesControl?.isDisabled}
              onClick={handleRemoveAllSlices}
              className={styles.removeAllButton}
            >
              <FormattedMessage
                id="toothCard.removeAllSlices"
                defaultMessage="Remove all"
              />
            </Button>
          )}

          {imageContainerComponent ? (
            <SlicesMIR
              className={styles.imageList}
              toothID={toothID}
              onClick={handleOpenModal}
            >
              {imageContainerComponent}
            </SlicesMIR>
          ) : (
            <SelectedSlicesMIR
              key={displaySlices.join(',')}
              toothID={toothID}
              className={styles.imageList}
              onClick={handleOpenModal}
              imageAttributes={{
                className: styles.slice,
                draggable: false,
                style: {
                  height: `${
                    isMobile
                      ? DEFAULT_MEDICAL_IMAGE_HEIGHT_MOBILE
                      : DEFAULT_MEDICAL_IMAGE_HEIGHT
                  }px`,
                },
              }}
            />
          )}
        </div>
      )}

      <footer className={styles.footer}>
        <div className={styles.leftFooterWrapper}>
          <div className={styles.buttons}>
            {!isShortCard && (
              <Button
                size={buttonSize}
                variant="gray"
                icon="plus"
                data-component="tooth-add-condition-button"
                onClick={() => handleAddCondition()}
              >
                {!isMobile && (
                  <FormattedMessage
                    id="toothCard.openAddConditionModalButton"
                    defaultMessage="Condition"
                  />
                )}
              </Button>
            )}

            <Button
              size={buttonSize}
              variant="gray"
              icon="pen"
              data-component="tooth-comment-button"
              onClick={
                isCommentEditorVisible
                  ? closeAndResetTextInEditor
                  : openChangeComment
              }
            >
              {!isMobile && (
                <FormattedMessage
                  id="toothCard.addCommentButton"
                  defaultMessage="Comment"
                />
              )}
            </Button>
          </div>
        </div>

        <div className={styles.rightFooterWrapper}>
          {reportType === ReportType.ReportType_CBCT_GP && showViewerButton && (
            <Button
              variant="tertiary"
              size={buttonSize}
              data-component="tooth-slices-button"
              onClick={handle3DSlicesClick}
            >
              <FormattedMessage id="toothCard.slices" defaultMessage="Slices" />
            </Button>
          )}
          {/* TODO: [2|m] refactor and move to ApproveToothButton feature */}
          <ApproveToothButton
            toothID={toothID}
            isApproved={!!isToothApproved}
            disabled={!report?.YourPermissions?.CanChangeToothApproved}
            isToothCard
            size={buttonSize}
          />
        </div>
      </footer>

      <CommentTooth
        autoFocus
        comment={displayComment}
        newComment={newComment}
        isNewCommentLoading={
          isNewCommentLoading ||
          toothSuggestedTreatment?.Status ===
            Tooth_SuggestedTreatmentStatus.IN_PROGRESS
        }
        isCommentEditorVisible={isCommentEditorVisible}
        onSaveComment={() => saveComment(toothID)}
        onCancel={closeAndResetTextInEditor}
        onOpenChangeComment={openChangeComment}
        onChangeCommentInEditor={changeCommentInEditor}
        toothID={toothID}
      />
    </WidgetCard>
  );
};

export const ToothCard = memo(InternalToothCard);
